/**
 * useVideoCall Hook
 *
 * Manages the Amazon Chime SDK session lifecycle for video calls.
 * Isolated from the audio call path so video errors cannot destabilise the Connect softphone.
 *
 * Key design decisions:
 * - Does NOT call audioVideo.bindAudioElement() — audio stays with Connect softphone.
 * - Camera is off by default on join — avoids unexpected permission dialogs.
 * - Background blur uses BackgroundBlurVideoFrameProcessor + DefaultVideoTransformDevice.
 *   The WASM/worker assets are loaded from Chime's CDN automatically.
 * - StrictMode-safe: leaveVideoMeeting() guards with meetingSessionRef check.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import {
    ConsoleLogger,
    DefaultDeviceController,
    DefaultMeetingSession,
    DefaultVideoTransformDevice,
    BackgroundBlurVideoFrameProcessor,
    LogLevel,
    MeetingSessionConfiguration,
    VideoTileState,
} from 'amazon-chime-sdk-js';
import type { ChimeMeetingInfo } from '../types/amazon-connect-streams';
import { connectClient } from '../services/connectClient';

// Derive the processor instance type from create()'s return — this is the BackgroundBlurProcessor
// interface which correctly declares process() and destroy(), unlike the class declaration.
type BlurProcessor = NonNullable<Awaited<ReturnType<typeof BackgroundBlurVideoFrameProcessor.create>>>;

export interface UseVideoCallResult {
    isVideoActive: boolean;
    isCameraOn: boolean;
    isBlurOn: boolean;
    remoteTileIds: number[];
    videoError: string | null;
    joinVideoMeeting: (info: ChimeMeetingInfo) => Promise<void>;
    leaveVideoMeeting: () => Promise<void>;
    toggleCamera: () => Promise<void>;
    toggleBlur: () => Promise<void>;
    localVideoRef: React.RefObject<HTMLVideoElement>;
    // Called from VideoCallPanel's ref callback to store + immediately bind a remote tile element
    bindRemoteTileElement: (tileId: number, el: HTMLVideoElement | null) => void;
}

export const useVideoCall = (): UseVideoCallResult => {
    const [isVideoActive, setIsVideoActive] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isBlurOn, setIsBlurOn] = useState(false);
    const [remoteTileIds, setRemoteTileIds] = useState<number[]>([]);
    const [videoError, setVideoError] = useState<string | null>(null);

    const meetingSessionRef = useRef<DefaultMeetingSession | null>(null);
    const loggerRef = useRef<ConsoleLogger | null>(null);
    const localVideoRef = useRef<HTMLVideoElement>(null);
    // Map of tileId → <video> DOM element; populated by VideoCallPanel's ref callbacks
    const remoteVideoEls = useRef<Map<number, HTMLVideoElement | null>>(new Map());
    const localTileIdRef = useRef<number | null>(null);
    // The raw MediaDeviceInfo selected for camera input — needed to rebuild the transform device on blur toggle
    const rawDeviceRef = useRef<MediaDeviceInfo | null>(null);
    // The active blur processor instance — must be destroyed when blur is turned off or camera stops
    const blurProcessorRef = useRef<BlurProcessor | null>(null);

    /** Destroy the blur processor and clear the ref. */
    const destroyBlurProcessor = useCallback(async () => {
        const processor = blurProcessorRef.current;
        if (!processor) return;
        blurProcessorRef.current = null;
        try { await processor.destroy(); } catch { /* ignore */ }
    }, []);

    /**
     * Start video input, optionally wrapping the device in a blur transform.
     * Centralises the logic so both toggleCamera and toggleBlur share the same path.
     */
    const startVideoInputWithBlur = useCallback(async (
        session: DefaultMeetingSession,
        device: MediaDeviceInfo,
        withBlur: boolean,
    ) => {
        await destroyBlurProcessor();

        if (withBlur) {
            const supported = await BackgroundBlurVideoFrameProcessor.isSupported();
            if (!supported) {
                setVideoError('Background blur is not supported in this browser');
                return;
            }
            const logger = loggerRef.current ?? new ConsoleLogger('BlurProcessor', LogLevel.WARN);
            const processor = await BackgroundBlurVideoFrameProcessor.create();
            if (!processor) throw new Error('Failed to initialise background blur processor');
            blurProcessorRef.current = processor;
            // Cast processor array to any[] — Chime SDK class types incompletely declare VideoFrameProcessor
            const transformDevice = new DefaultVideoTransformDevice(logger, device, [processor] as any[]);
            await session.audioVideo.startVideoInput(transformDevice as any);
        } else {
            await session.audioVideo.startVideoInput(device);
        }
    }, [destroyBlurProcessor]);

    const leaveVideoMeeting = useCallback(async () => {
        const session = meetingSessionRef.current;
        if (!session) return;

        try { session.audioVideo.stopLocalVideoTile(); } catch { /* ignore */ }
        try { await (session.audioVideo as any).stopVideoInput?.(); } catch { /* ignore */ }
        try { session.audioVideo.stop(); } catch { /* ignore */ }
        await destroyBlurProcessor();

        meetingSessionRef.current = null;
        localTileIdRef.current = null;
        rawDeviceRef.current = null;
        remoteVideoEls.current.clear();
        setIsVideoActive(false);
        setIsCameraOn(false);
        setIsBlurOn(false);
        setRemoteTileIds([]);
        setVideoError(null);
    }, [destroyBlurProcessor]);

    const joinVideoMeeting = useCallback(async (info: ChimeMeetingInfo) => {
        if (meetingSessionRef.current) {
            await leaveVideoMeeting();
        }
        setVideoError(null);

        try {
            const logger = new ConsoleLogger('VideoCall', LogLevel.WARN);
            loggerRef.current = logger;
            const deviceController = new DefaultDeviceController(logger);
            const configuration = new MeetingSessionConfiguration(
                info.meeting as any,
                info.attendee as any
            );
            const session = new DefaultMeetingSession(configuration, logger, deviceController);
            meetingSessionRef.current = session;

            // Register video tile observer (AudioVideoObserver)
            session.audioVideo.addObserver({
                videoTileDidUpdate: (tileState: VideoTileState) => {
                    if (tileState.tileId == null) return;
                    if (!tileState.boundAttendeeId) return;

                    if (tileState.localTile) {
                        localTileIdRef.current = tileState.tileId;
                        const el = localVideoRef.current;
                        if (el) {
                            session.audioVideo.bindVideoElement(tileState.tileId, el);
                        }
                    } else {
                        // Add to state so VideoCallPanel renders a <video> element.
                        // The ref callback (bindRemoteTileElement) will bind it once the DOM mounts.
                        setRemoteTileIds(prev =>
                            prev.includes(tileState.tileId!) ? prev : [...prev, tileState.tileId!]
                        );
                        // If the element is already mounted (e.g. tile update for existing tile), bind now
                        const el = remoteVideoEls.current.get(tileState.tileId!);
                        if (el) {
                            session.audioVideo.bindVideoElement(tileState.tileId!, el);
                        }
                    }
                },
                videoTileWasRemoved: (tileId: number) => {
                    if (tileId === localTileIdRef.current) {
                        localTileIdRef.current = null;
                    } else {
                        setRemoteTileIds(prev => prev.filter(id => id !== tileId));
                        remoteVideoEls.current.delete(tileId);
                    }
                },
            });

            // Start signalling + media — deliberately no bindAudioElement() call
            // so audio stays with the Connect softphone, not Chime's audio bridge.
            session.audioVideo.start();
            setIsVideoActive(true);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setVideoError(`Failed to join video meeting: ${msg}`);
        }
    }, [leaveVideoMeeting]);

    const toggleCamera = useCallback(async () => {
        const session = meetingSessionRef.current;
        if (!session) return;

        try {
            if (isCameraOn) {
                session.audioVideo.stopLocalVideoTile();
                await (session.audioVideo as any).stopVideoInput?.();
                await destroyBlurProcessor();
                rawDeviceRef.current = null;
                setIsCameraOn(false);
            } else {
                // Chime SDK v3: must select a video input device before starting the local tile
                const devices = await session.audioVideo.listVideoInputDevices();
                if (devices.length === 0) {
                    setVideoError('No camera device found');
                    return;
                }
                const device = devices[0];
                rawDeviceRef.current = device;
                await startVideoInputWithBlur(session, device, isBlurOn);
                session.audioVideo.startLocalVideoTile();
                setIsCameraOn(true);
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setVideoError(`Camera error: ${msg}`);
        }
    }, [isCameraOn, isBlurOn, destroyBlurProcessor, startVideoInputWithBlur]);

    /**
     * Toggle background blur. If the camera is already on, restarts video input with/without
     * the blur transform device. If the camera is off, the flag is stored and applied next
     * time the camera is turned on.
     * Also calls agent.setBackgroundBlur() to broadcast the state change to Connect.
     */
    const toggleBlur = useCallback(async () => {
        const newBlurState = !isBlurOn;
        const session = meetingSessionRef.current;

        if (isCameraOn && session && rawDeviceRef.current) {
            try {
                // Restart video input with the new blur state
                session.audioVideo.stopLocalVideoTile();
                await (session.audioVideo as any).stopVideoInput?.();
                await startVideoInputWithBlur(session, rawDeviceRef.current, newBlurState);
                session.audioVideo.startLocalVideoTile();
            } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                setVideoError(`Blur error: ${msg}`);
                return;
            }
        }

        setIsBlurOn(newBlurState);

        // Broadcast the state change to Connect
        try {
            const agent = new connectClient.Agent();
            if (agent) (agent as any).setBackgroundBlur?.(newBlurState);
        } catch { /* ignore if agent not available */ }
    }, [isBlurOn, isCameraOn, startVideoInputWithBlur]);

    /**
     * Called by VideoCallPanel's ref callback for each remote <video> element.
     * Stores the DOM element AND immediately binds the Chime tile if the session is active.
     * This resolves the race where videoTileDidUpdate fires before the DOM element mounts.
     */
    const bindRemoteTileElement = useCallback((tileId: number, el: HTMLVideoElement | null) => {
        remoteVideoEls.current.set(tileId, el);
        const session = meetingSessionRef.current;
        if (session && el) {
            try {
                session.audioVideo.bindVideoElement(tileId, el);
            } catch { /* ignore */ }
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            const session = meetingSessionRef.current;
            if (!session) return;
            try { session.audioVideo.stopLocalVideoTile(); } catch { /* ignore */ }
            try { (session.audioVideo as any).stopVideoInput?.(); } catch { /* ignore */ }
            try { session.audioVideo.stop(); } catch { /* ignore */ }
            void blurProcessorRef.current?.destroy();
            meetingSessionRef.current = null;
        };
    }, []);

    return {
        isVideoActive,
        isCameraOn,
        isBlurOn,
        remoteTileIds,
        videoError,
        joinVideoMeeting,
        leaveVideoMeeting,
        toggleCamera,
        toggleBlur,
        localVideoRef,
        bindRemoteTileElement,
    };
};

export default useVideoCall;
